"use client";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useQueryState } from "nuqs";

import { SignInComponent } from "@/components/auth/SignInComponent";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { SpinnerFullPage } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSession } from "@/lib/auth/client";
import { normalizeDestination } from "@/lib/deep-linking";
import { callbackUrlIsSafe } from "@/lib/utils-client";

export default function SignInPage() {
  const { data: session, isPending } = useSession();
  const router = useRouter();

  const [destinationParam] = useQueryState("destination", {
    defaultValue: "",
  });

  // Show loading spinner while checking session
  if (isPending) {
    return <SpinnerFullPage />;
  }

  // Show spinner while redirecting authenticated users
  if (session?.user) {
    // Normalize destination from any encoding format, then validate
    const normalized = normalizeDestination(destinationParam);
    const safeDestination = callbackUrlIsSafe(normalized) ? normalized : "/";
    router.push(safeDestination || "/");
    return <SpinnerFullPage />;
  }

  return (
    <div className="grid min-h-screen grid-cols-1 bg-background md:grid-cols-2">
      {/* Left Column - Login Form */}
      <div className="flex flex-col px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-center md:justify-start">
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                href="https://www.apartmentdibs.com"
                className="flex items-center gap-2 transition-opacity hover:opacity-85"
                aria-label="ApartmentDibs Website"
              >
                <Image
                  src="/AD_logo.svg"
                  alt="ApartmentDibs"
                  width={50}
                  height={32}
                  priority
                  className="h-[22px] justify-start dark:brightness-0 dark:invert"
                />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              <p>ApartmentDibs</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Login form centered vertically in remaining space */}
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm">
            <SignInComponent
              destination={normalizeDestination(destinationParam)}
              variant="page"
            />
          </div>
        </div>

        {/* ItemAvatar component */}
        <div className="mb-4 flex justify-center">
          <div className="flex w-full max-w-sm flex-col gap-6">
            <Item variant="outline" className="grid grid-cols-[1fr_auto] gap-4">
              <div className="flex flex-col gap-3">
                <ItemContent>
                  <ItemTitle className="font-mono font-medium">
                    Want a demo?
                  </ItemTitle>
                  <ItemDescription>
                    Schedule a call with one of our experts.
                  </ItemDescription>
                </ItemContent>
                <ItemActions>
                  <Button asChild size="sm" variant="outline" className="mt-1">
                    <Link
                      href="https://calendar.app.google/LoBEgUuvrAwULjZe9"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Schedule a call
                    </Link>
                  </Button>
                </ItemActions>
              </div>
              <ItemMedia>
                <div className="flex -space-x-2 *:data-[slot=avatar]:ring-2 *:data-[slot=avatar]:ring-background *:data-[slot=avatar]:grayscale">
                  <Avatar className="hidden sm:flex">
                    <AvatarImage src="/jakub_schedule_avatar.jpeg" alt="JM" />
                    <AvatarFallback>JM</AvatarFallback>
                  </Avatar>
                  <Avatar>
                    <AvatarImage src="/emily_schedule_avatar.jpeg" alt="EP" />
                    <AvatarFallback>EP</AvatarFallback>
                  </Avatar>
                </div>
              </ItemMedia>
            </Item>
          </div>
        </div>
      </div>

      {/* Right Column - Branded Background */}
      <div
        className="m-4 hidden rounded-none bg-cover bg-center bg-no-repeat md:flex"
        style={{ backgroundImage: "url('/aptdibs_login.png')" }}
      ></div>
    </div>
  );
}
